import { type FormEvent, type ReactNode, useEffect, useRef } from "react";

import { Button } from "./button";
import { ErrorSummary } from "./error-summary";
import { focusFirstInvalid } from "./fields";
import { type FormMessage, StatusMessage } from "./status-message";

type SectionFormProps = Readonly<{
  /** The label of the form, for a screen reader. */
  label: string;
  dirty: boolean;
  pending: boolean;
  /** No save bar: the fields are read-only. */
  readOnly: boolean;
  message: FormMessage | null;
  errorFields: readonly Readonly<{ id: string; label: string }>[];
  /** Counts failed checks; focus moves to the first invalid control each time it grows. */
  errorRevision: number;
  onSubmit: () => void;
  onDiscard: () => void;
  saveLabel?: string;
  /** The form can be submitted although nothing is dirty (a create form). */
  alwaysSubmittable?: boolean;
  children: ReactNode;
}>;

/** One independently saved form: fields, an error summary, a save bar with a dirty marker and the result message. */
export function SectionForm({
  label,
  dirty,
  pending,
  readOnly,
  message,
  errorFields,
  errorRevision,
  onSubmit,
  onDiscard,
  saveLabel = "Save changes",
  alwaysSubmittable = false,
  children,
}: SectionFormProps) {
  const form = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (errorRevision > 0) focusFirstInvalid(form.current);
  }, [errorRevision]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!readOnly) onSubmit();
  }

  return (
    <form
      ref={form}
      // `label` must be a fixed phrase ("Common fields"), never text a person typed: error reports copy attributes.
      aria-label={label}
      noValidate
      onSubmit={handleSubmit}
      className="grid gap-4"
    >
      <ErrorSummary fields={errorFields} />
      {children}
      {readOnly ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={pending || (!dirty && !alwaysSubmittable)}>
            {pending ? "Saving…" : saveLabel}
          </Button>
          {dirty ? (
            <Button type="button" variant="secondary" onClick={onDiscard} disabled={pending}>
              Discard changes
            </Button>
          ) : null}
          {dirty ? (
            <span className="text-sm font-semibold text-warning">
              <span aria-hidden="true">● </span>Unsaved changes
            </span>
          ) : null}
        </div>
      )}
      <StatusMessage message={message} />
    </form>
  );
}
